import { tl } from '@doda/common/src/utils/adobeAnalyticsUtil';
import { B4R_TSUUKA_AB_TEST_GROUP } from '../../constants';
import { B4rTsuukaAbTestPattern } from '../../types/analytics';

/**
 * Determine AB test type from group number
 * DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
 *
 * @param groupNo - AB test group number from getAbtestGroupNo API
 * @returns AB test type (ORIGINAL, TEST, or FAILED)
 */
export const getB4rTsuukaABTestPattern = (
  groupNo: number | undefined
): B4rTsuukaAbTestPattern => {
  switch (groupNo) {
    case 1:
      return B4R_TSUUKA_AB_TEST_GROUP.ORIGINAL;
    case 2:
      return B4R_TSUUKA_AB_TEST_GROUP.TEST;
    default:
      return B4R_TSUUKA_AB_TEST_GROUP.FAILED;
  }
};

/**
 * Get the appropriate recommendation type based on AB test pattern
 * DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
 * @param b4rTsuukaABTestPattern - The AB test pattern (ORIGINAL, TEST, or FAILED)
 * @returns The recommendation type string to be used for fetching recommendations
 */
export const getApplicationRecommendType = (
  b4rTsuukaABTestPattern: B4rTsuukaAbTestPattern
) =>
  b4rTsuukaABTestPattern === B4R_TSUUKA_AB_TEST_GROUP.TEST
    ? 'b4r_cbm_gousei_rerank_recommend'
    : 'AWS_Recommend_oubo';

/**
 * Get the appropriate effect name based on AB test pattern
 * DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
 * @param b4rTsuukaABTestPattern - The AB test pattern (ORIGINAL, TEST, or FAILED)
 * @param testEffectName - The effect name for test pattern
 * @param originalEffectName - The effect name for original pattern
 * @returns The effect name string to be used for fetching recommendations
 */
export const getApplicationEffectName = (
  b4rTsuukaABTestPattern: B4rTsuukaAbTestPattern,
  testEffectName: string,
  originalEffectName: string
) =>
  b4rTsuukaABTestPattern === B4R_TSUUKA_AB_TEST_GROUP.TEST
    ? testEffectName
    : originalEffectName;

/**
 * B4R Tsuuka Recommend AB Test Link Names
 * DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
 */
const B4R_TSUUKA_RECOMMEND_LINK_NAMES = {
  [B4R_TSUUKA_AB_TEST_GROUP.ORIGINAL]:
    'ab_17171_ichiran_b4rtsuukarecommend_original',
  [B4R_TSUUKA_AB_TEST_GROUP.TEST]: 'ab_17171_ichiran_b4rtsuukarecommend_test',
} as const;

/**
 * Measure B4R Tsuuka Recommend AB Test
 * DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
 *
 * This function measures the AB test for B4R×通過スコアモデル vs 応募B4R recommendation.
 * It only fires measurement when:
 * 1. Recommend type is 'b4r_cbm_gousei_rerank_recommend' or 'AWS_Recommend_oubo'
 * 2. AB test group is not FAILED
 * 3. Adobe Analytics script is loaded
 *
 * @param recommendType - The type of recommendation
 * @param b4rTsuukaABTestPattern - AB test pattern (ORIGINAL or TEST)
 * @param isAdobeScript - Whether Adobe Analytics script is loaded
 */
export const measureB4rTsuukaRecommend = (
  recommendType: string | undefined,
  b4rTsuukaABTestPattern: B4rTsuukaAbTestPattern,
  isAdobeScript: boolean
): void => {
  // NOTE: レコメンドがB4R×通過スコアモデルまたは応募B4Rではない場合計測発火しない
  if (
    recommendType !== 'b4r_cbm_gousei_rerank_recommend' &&
    recommendType !== 'AWS_Recommend_oubo'
  ) {
    return;
  }

  // NOTE: ABテスト groupNo 失敗時は計測発火しない
  if (
    b4rTsuukaABTestPattern === B4R_TSUUKA_AB_TEST_GROUP.FAILED ||
    !isAdobeScript
  ) {
    return;
  }

  const linkName = B4R_TSUUKA_RECOMMEND_LINK_NAMES[b4rTsuukaABTestPattern];

  tl(linkName, {
    pageName: '求人一覧（通常）',
  });
};
